import { useState, type ReactNode } from "react";
import { useI18n } from "@expertloop/i18n/react";
import type { MessageKey } from "@expertloop/i18n";
import type { MediationCaseView, MediationOpener } from "@expertloop/shared";
import { adminApi } from "../../services/api";
import { CollapsibleCard } from "./CollapsibleCard";

interface MediationCasePanelProps {
	orderId: string;
	mediationCase: MediationCaseView;
	/** Texte d'une décision dans la langue de lecture choisie (LANG-3), avec sa mention. */
	decisionText?: (roundNumber: number, original: string) => { text: string; note: ReactNode };
	/** Rechargement du détail de la conversation après une réouverture. */
	onReopened: () => void;
}

const OPENER_KEYS: Record<MediationOpener, MessageKey> = {
	expert: "adminChatDetail.mediationCase.openedBy.expert",
	client: "adminChatDetail.mediationCase.openedBy.client",
	support: "adminChatDetail.mediationCase.openedBy.support",
};

const BLOCKED_KEYS: Record<string, MessageKey> = {
	not_in_mediation: "adminChatDetail.mediationCase.blocked.not_in_mediation",
	not_resolved: "adminChatDetail.mediationCase.blocked.not_resolved",
	order_not_captured: "adminChatDetail.mediationCase.blocked.order_not_captured",
	nothing_left: "adminChatDetail.mediationCase.blocked.nothing_left",
	already_reopened: "adminChatDetail.mediationCase.blocked.already_reopened",
	order_not_found: "adminChatDetail.mediationCase.blocked.order_not_found",
};

function formatDate(value: Date | string): string {
	return new Date(value).toLocaleString();
}

/**
 * Dossier de médiation (MEDIATION-1) : les tours, ce qui est nouveau depuis la dernière
 * lecture de ce membre du support, et la réouverture d'un litige tranché. La
 * réouverture passe par une fenêtre de dialogue, jamais par une confirmation du navigateur.
 */
export function MediationCasePanel({ orderId, mediationCase, decisionText, onReopened }: MediationCasePanelProps) {
	const { t } = useI18n();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [reason, setReason] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");

	const newCount = mediationCase.newMessageIds.length + mediationCase.newDeliveryIds.length;

	const closeDialog = () => {
		if (submitting) return;
		setDialogOpen(false);
		setError("");
	};

	const submit = async () => {
		if (!reason.trim()) {
			setError(t("adminChatDetail.mediationCase.reopenReasonRequired"));
			return;
		}
		setSubmitting(true);
		setError("");
		try {
			await adminApi.post(`/admin/mediations/${orderId}/reopen`, { reason: reason.trim() });
			setDialogOpen(false);
			setReason("");
			onReopened();
		} catch (err) {
			const code = err instanceof Error ? err.message : "";
			const key = BLOCKED_KEYS[code];
			setError(key ? t(key) : t("adminChatDetail.mediationCase.reopenFailed"));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
		<CollapsibleCard
			className="mediation-case-panel"
			id="mediation-case"
			title={t("adminChatDetail.mediationCase.title")}
			headerExtra={
				mediationCase.canReopen ? (
					<button className="btn btn-sm btn-outline" onClick={() => setDialogOpen(true)}>
						{t("adminChatDetail.mediationCase.reopen")}
					</button>
				) : undefined
			}
		>
			<div>
				<p className="mediation-case-review">
					{mediationCase.previousReviewAt
						? t("adminChatDetail.mediationCase.lastRead", { date: formatDate(mediationCase.previousReviewAt) })
						: t("adminChatDetail.mediationCase.firstRead")}
					{newCount > 0 && (
						<span className="new-badge" style={{ marginLeft: "0.5rem" }}>
							{t("adminChatDetail.mediationCase.newSince", { count: newCount })}
						</span>
					)}
				</p>
				<ol className="mediation-case-rounds">
					{mediationCase.rounds.map((round) => (
						<li key={round.number} className="mediation-case-round">
							<strong>{t("adminChatDetail.mediationCase.round", { number: round.number })}</strong>
							<span> — {t(OPENER_KEYS[round.openedBy])} ({formatDate(round.openedAt)})</span>
							{round.reopenedBy && (
								<div>{t("adminChatDetail.mediationCase.reopenedBy", { name: round.reopenedBy.adminEmail ?? round.reopenedBy.adminId })}</div>
							)}
							{round.reason && <blockquote className="mediation-reason-quote">{round.reason}</blockquote>}
							<div className="mediation-case-decision">
								{round.decision ? (
									<>
										<span className={`status-badge ${round.decision.decision === "favor_client" ? "completed" : "pending"}`}>
											{t(`adminChatDetail.mediationCase.decision.${round.decision.decision}`)}
										</span>{" "}
										{t("adminChatDetail.mediationCase.decidedAt", { date: formatDate(round.decision.resolvedAt) })}
										<blockquote className="mediation-reason-quote">
											{decisionText ? decisionText(round.number, round.decision.message).text : round.decision.message}
											{decisionText ? decisionText(round.number, round.decision.message).note : null}
										</blockquote>
									</>
								) : (
									<span className="status-badge mediation">{t("adminChatDetail.mediationCase.noDecisionYet")}</span>
								)}
							</div>
						</li>
					))}
				</ol>
			</div>
		</CollapsibleCard>

			{dialogOpen && (
				<div className="modal-overlay" onClick={closeDialog}>
					<div
						className="modal-content"
						style={{ maxWidth: "500px" }}
						role="dialog"
						aria-modal="true"
						aria-labelledby="reopen-mediation-title"
						onClick={(event) => event.stopPropagation()}
					>
						<div className="modal-header">
							<h2 id="reopen-mediation-title">{t("adminChatDetail.mediationCase.reopenTitle")}</h2>
							<button className="modal-close" onClick={closeDialog} aria-label={t("common.cancel")}>
								&times;
							</button>
						</div>
						<div className="modal-body">
							<p>{t("adminChatDetail.mediationCase.reopenBody")}</p>
							<div className="form-group">
								<label htmlFor="reopen-mediation-reason">{t("adminChatDetail.mediationCase.reopenReasonLabel")}</label>
								<textarea
									id="reopen-mediation-reason"
									value={reason}
									maxLength={2000}
									rows={4}
									onChange={(event) => setReason(event.target.value)}
									disabled={submitting}
								/>
							</div>
							{error && <div className="error-message" role="alert">{error}</div>}
						</div>
						<div className="modal-footer">
							<button className="btn btn-secondary" onClick={closeDialog} disabled={submitting}>
								{t("common.cancel")}
							</button>
							<button className="btn btn-primary" onClick={submit} disabled={submitting}>
								{submitting ? t("adminChatDetail.mediationCase.reopening") : t("adminChatDetail.mediationCase.reopenConfirm")}
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
